import { useEffect, useState } from "react"

import { useGetDevicesQuery } from "store/api/devicesApi"
import { ReactComponent as IconReset } from "assets/svg/arrow-path.svg"

import { ModalLayout } from "layouts/ModalLayout"
import { AlertRuleToggleConfirmationModal } from "pages/contractor/MonitoringPage/AlertRuleToggleConfirmationModal"

import {
  alertRuleModalConfigs,
  commonThresholdDescription,
  getInitialDraftForVariant,
  getTemperatureRange,
} from "./config"
import { mapDevicesToTemperatureRuleThermostats } from "./temperatureRuleThermostats"
import { TemperatureRuleThermostatsTab } from "./TemperatureRuleThermostatsTab"
import { AlertRuleDraft, AlertRuleVariant } from "./types"

import styles from "./index.module.scss"

type AlertRuleTab = "rule" | "thermostats"

interface PendingThermostatAction {
  nextIsActive: boolean
}

const isTemperatureVariant = (variant: AlertRuleVariant | null) =>
  variant === "highTemperature" || variant === "lowTemperature"

const ALERT_RULE_INPUT_ABSOLUTE_MAX = 1000

const normalizeThresholdValue = (value: string, unit: string | undefined) => {
  if (value.trim() === "" || Number.isNaN(Number(value))) return value

  return unit === "C" ? String(Number(Number(value).toFixed(2))) : String(Math.round(Number(value)))
}

const normalizeDraft = (variant: AlertRuleVariant | null, draft: AlertRuleDraft) =>
  isTemperatureVariant(variant)
    ? { ...draft, thresholdValue: normalizeThresholdValue(draft.thresholdValue, draft.thresholdUnit) }
    : draft

const validateDraft = (variant: AlertRuleVariant, draft: AlertRuleDraft) => {
  const value = Number(draft.thresholdValue)

  if (draft.thresholdValue.trim() === "" || !Number.isFinite(value)) {
    return "Enter a valid threshold value."
  }

  const thresholdRange = isTemperatureVariant(variant)
    ? getTemperatureRange(draft.thresholdUnit)
    : { min: 0, max: ALERT_RULE_INPUT_ABSOLUTE_MAX }

  if (value < thresholdRange.min || value > thresholdRange.max) {
    if (isTemperatureVariant(variant)) {
      return draft.thresholdUnit === "C"
        ? "Temperature threshold must be between 0°C and 37.77°C."
        : "Temperature threshold must be between 32°F and 100°F."
    }

    return "Value must be between 0 and 1000."
  }

  const durationValue = Number(draft.durationValue)
  if (
    draft.durationValue.trim() === "" ||
    !Number.isFinite(durationValue) ||
    durationValue < 0 ||
    durationValue > ALERT_RULE_INPUT_ABSOLUTE_MAX
  ) {
    return "Duration must be between 0 and 1000."
  }

  return undefined
}

interface AlertRuleEditModalProps {
  variant: AlertRuleVariant | null
  initialDraft?: AlertRuleDraft | null
  onClose: () => void
  onSave?: (variant: AlertRuleVariant, draft: AlertRuleDraft) => void | Promise<void>
  onBulkApply?: (deviceIds: number[], nextIsActive: boolean) => void | Promise<void>
  onApplyToAllThermostats?: (draft: AlertRuleDraft) => boolean | void | Promise<boolean | void>
  confirmButtonText?: string
}

const AlertRuleEditModal = ({
  variant,
  initialDraft,
  onClose,
  onSave,
  onBulkApply,
  onApplyToAllThermostats,
  confirmButtonText = "Save",
}: AlertRuleEditModalProps) => {
  const config = variant ? alertRuleModalConfigs[variant] : null
  const [draft, setDraft] = useState<AlertRuleDraft | null>(
    config ? normalizeDraft(variant, initialDraft || getInitialDraftForVariant(variant as AlertRuleVariant)) : null,
  )
  const [activeTab, setActiveTab] = useState<AlertRuleTab>("rule")
  const [selectedThermostatIds, setSelectedThermostatIds] = useState<number[]>([])
  const [pendingThermostatAction, setPendingThermostatAction] = useState<PendingThermostatAction | null>(null)
  const [thermostats, setThermostats] = useState<ReturnType<typeof mapDevicesToTemperatureRuleThermostats>>([])
  const initialDraftSignature = JSON.stringify(initialDraft)

  const hasThermostatsTab =
    (variant === "highTemperature" || variant === "lowTemperature") && typeof onBulkApply === "function"
  const hasApplyToAllThermostats = typeof onApplyToAllThermostats === "function"
  const { data: devicesData } = useGetDevicesQuery(
    { phisical_status: "installed", per_page: 1000 },
    { skip: !hasThermostatsTab },
  )

  useEffect(() => {
    setThermostats(mapDevicesToTemperatureRuleThermostats(devicesData?.data))
  }, [devicesData?.data])

  useEffect(() => {
    setDraft(
      config ? normalizeDraft(variant, initialDraft || getInitialDraftForVariant(variant as AlertRuleVariant)) : null,
    )
    setActiveTab("rule")
    setSelectedThermostatIds([])
    setPendingThermostatAction(null)
  }, [config, initialDraftSignature, variant])

  const updateDraft = (patch: Partial<AlertRuleDraft>) => {
    setDraft(current => (current ? { ...current, ...patch } : current))
  }

  if (!config || !draft) return null

  const thresholdError = validateDraft(variant as AlertRuleVariant, draft)
  const thresholdErrorId = isTemperatureVariant(variant)
    ? "Temperature threshold-error"
    : variant === "highHumidity" || variant === "lowHumidity"
      ? "Humidity threshold-error"
      : undefined

  const handleSave = async () => {
    const normalizedDraft = normalizeDraft(variant, draft)
    if (validateDraft(variant as AlertRuleVariant, normalizedDraft)) return

    await onSave?.(variant as AlertRuleVariant, normalizedDraft)
    onClose()
  }

  const handleApplyToAllThermostats = async () => {
    const normalizedDraft = normalizeDraft(variant, draft)
    if (validateDraft(variant as AlertRuleVariant, normalizedDraft)) return

    const shouldClose = await onApplyToAllThermostats?.(normalizedDraft)

    if (shouldClose !== false) {
      onClose()
    }
  }

  const handleReset = () => {
    setDraft(normalizeDraft(variant, initialDraft || getInitialDraftForVariant(variant as AlertRuleVariant)))
  }

  const handleToggleThermostat = (thermostatId: number) => {
    setSelectedThermostatIds(current =>
      current.includes(thermostatId) ? current.filter(id => id !== thermostatId) : [...current, thermostatId],
    )
  }

  const handleToggleAllThermostats = () => {
    setSelectedThermostatIds(current => (current.length === thermostats.length ? [] : thermostats.map(item => item.id)))
  }

  const handleConfirmThermostatAction = () => {
    if (!pendingThermostatAction) return

    const nextIsActive = pendingThermostatAction.nextIsActive

    Promise.resolve(onBulkApply?.(selectedThermostatIds, nextIsActive)).finally(() => {
      setThermostats(current =>
        current.map(thermostat =>
          selectedThermostatIds.includes(thermostat.id)
            ? {
                ...thermostat,
                isActive: nextIsActive,
              }
            : thermostat,
        ),
      )
      setSelectedThermostatIds([])
      setPendingThermostatAction(null)
    })
  }

  return (
    <>
      <ModalLayout
        className={styles.alertRuleModal}
        closeIconClassName={styles.alertRuleCloseIcon}
        footerClassName={styles.alertRuleModalFooter}
        width="800px"
        onClose={onClose}
        onCancel={activeTab === "rule" ? (hasApplyToAllThermostats ? handleApplyToAllThermostats : onClose) : undefined}
        onConfirm={activeTab === "rule" ? handleSave : undefined}
        confirmButtonText={confirmButtonText}
        cancelButtonText={hasApplyToAllThermostats ? "Apply to All Thermostats" : "Close"}
        isDisabled={activeTab === "rule" && Boolean(thresholdError)}
        isCancelDisabled={hasApplyToAllThermostats && activeTab === "rule" && Boolean(thresholdError)}
        isWithoutFooter={activeTab === "thermostats"}
      >
        <div className={styles.modalHeader}>
          <div className={styles.ruleIcon}>(x)</div>
          <h3 className={styles.modalTitle}>{config.title}</h3>
        </div>

        {hasThermostatsTab && (
          <div className={styles.tabSwitch}>
            <button
              type="button"
              className={styles.tabButton}
              data-active={activeTab === "rule"}
              onClick={() => setActiveTab("rule")}
            >
              Rule
            </button>
            <button
              type="button"
              className={styles.tabButton}
              data-active={activeTab === "thermostats"}
              onClick={() => setActiveTab("thermostats")}
            >
              Thermostats
            </button>
          </div>
        )}

        {activeTab === "rule" && (
          <>
            <div className={styles.thresholdBlock}>
              <div className={styles.blockLabel}>THRESHOLD</div>
              <div className={styles.blockDescription}>{commonThresholdDescription}</div>
            </div>

            <div className={styles.conditionCard}>
              <div className={styles.conditionText}>{config.renderSentence(draft, updateDraft, thresholdError)}</div>
              {thresholdError && thresholdErrorId && (
                <div id={thresholdErrorId} role="alert" className={styles.valueUnitError}>
                  {thresholdError}
                </div>
              )}
            </div>

            {config.info && (
              <div className={styles.infoCard}>
                {config.info.map(line => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            )}

            <button type="button" className={styles.resetButton} onClick={handleReset}>
              <IconReset className={styles.resetIcon} />
              Reset to Default
            </button>
          </>
        )}

        {activeTab === "thermostats" && (
          <TemperatureRuleThermostatsTab
            thermostats={thermostats}
            selectedThermostatIds={selectedThermostatIds}
            onToggleThermostat={handleToggleThermostat}
            onToggleAllThermostats={handleToggleAllThermostats}
            onClearSelection={() => setSelectedThermostatIds([])}
            onActionClick={nextIsActive => setPendingThermostatAction({ nextIsActive })}
          />
        )}
      </ModalLayout>

      {pendingThermostatAction && (
        <AlertRuleToggleConfirmationModal
          isActive={!pendingThermostatAction.nextIsActive}
          targetDescription="the selected thermostats"
          impactMessage={null}
          onConfirm={handleConfirmThermostatAction}
          onCancel={() => setPendingThermostatAction(null)}
        />
      )}
    </>
  )
}

export default AlertRuleEditModal
